import NiceModal, { useModal } from '@ebay/nice-modal-react';
import dayjs from 'dayjs';

import { useCallback, useEffect, useState } from 'react';
import { Trans } from 'react-i18next';

import Button from 'components/field/Button';
import OTPInput from 'components/field/OTPInput';
import FormItem from 'components/form/FormItem';
import Alert from 'components/global/Alert';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';

import useAxiosPrivate from 'hooks/useAxiosPrivate';

import { PROFILE } from 'services/consts';

const OTPModal = NiceModal.create(({ data, sendOTPCode, verifyOTP, loadingVerify, isMail }) => {
	const modal = useModal();
	const axiosPrivate = useAxiosPrivate();

	const [value, setValue] = useState([null, null, null, null]);
	const [loading, setLoading] = useState(false);
	const [isVerifyCodeLoading, setIsVerifyCodeLoading] = useState(false);

	const handleVerifyOTP = (code) => {
		setIsVerifyCodeLoading(true);
		verifyOTP(modal, code.join('')).finally(() => {
			setIsVerifyCodeLoading(false);
		});
	};

	const handleVerifyOTPforDev = () => {
		setIsVerifyCodeLoading(true);
		axiosPrivate
			.post(PROFILE.VERIFY_USER_PHONE_ONLY_DEV, { phone: data })
			.then((res) => {
				if (res.status === 200) {
					modal.resolve({ verified: true });
					modal.remove();
				}
			})
			.finally(() => {
				setIsVerifyCodeLoading(false);
			});
	};

	return (
		<Modal fullwidth maxWidth="x-small" className="modal-otp">
			<ModalTitle>
				<Typography variant="p1" fontWeight="500">
					mobileNumber.sendCode
				</Typography>
			</ModalTitle>
			<ModalContent style={{ padding: '1rem' }}>
				<Typography fontWeight="500">mobileNumber.OTPVerify</Typography>
				<Typography noWrap className="mt-2">
					<Trans i18nKey="mobileNumber.OTPCode" values={{ phoneNumber: data }} />
				</Typography>

				<FormItem
					childStyle={{
						display: 'flex',
						gap: '1.5rem',
						flexDirection: 'column',
						alignItems: 'center'
					}}>
					<OTPInput
						value={value}
						setValue={setValue}
						loading={loading || isVerifyCodeLoading}
						onFilledOnce={handleVerifyOTP}
					/>
				</FormItem>
				<FormItem
					childStyle={{ display: 'flex', gap: '1.5rem', flexDirection: 'column', alignItems: 'center' }}>
					<Timer loading={loading} setLoading={setLoading} sendOTPCode={sendOTPCode} />
				</FormItem>

				{import.meta.env.VITE_SANDBOX === 'true' && !isMail && (
					<Alert showIcon variant="info">
						<FormItem
							style={{ padding: '0' }}
							childStyle={{
								display: 'flex',
								gap: '1.5rem',
								flexDirection: 'column'
							}}>
							<Typography noWrap>sandboxArea</Typography>
							<Button
								label="instantVerify"
								onClick={handleVerifyOTPforDev}
								loading={loading || isVerifyCodeLoading}
							/>
						</FormItem>
					</Alert>
				)}
			</ModalContent>
			<ModalActions>
				<Button
					variant="outline"
					label="cancel"
					onClick={() => {
						modal.resolve({ verified: false });
						modal.remove();
					}}
				/>
				<Button
					disabled={value.join('').length !== 4 || loadingVerify}
					label="verify"
					onClick={() => handleVerifyOTP(value)}
					loading={loading || isVerifyCodeLoading}
				/>
			</ModalActions>
		</Modal>
	);
});

const Timer = ({ loading, setLoading, sendOTPCode }) => {
	const defaultWaitTime = 2 * 60;
	const [time, setTime] = useState(defaultWaitTime);

	useEffect(() => {
		let timeout;
		if (time >= 0) {
			timeout = setInterval(() => {
				setTime((prevTime) => prevTime - 1);
			}, 1000);
		}

		return () => clearInterval(timeout);
	}, [loading]);

	const handleResend = useCallback(async () => {
		setLoading(true);
		const token = await sendOTPCode();
		if (token) {
			setTime(defaultWaitTime);
		}
		setLoading(false);
	}, []);

	return time >= 0 && !loading ? (
		<Typography>{dayjs.duration(time, 'seconds').format('mm:ss')}</Typography>
	) : (
		<Button label="resend" variant="text" onClick={handleResend} disabled={loading} />
	);
};

export default OTPModal;
